import type { TFunction } from 'i18next';
import type { WalletHighlight, WalletUpcomingPayments } from '../../../../queries/pxtraCard';
import { monthYearName, positionLabel } from './format';

export type UpcomingRow = {
  key: string;
  label: string;
  persons: number;
  /** Null where the row has no single date, as with the open positions before „Von“. */
  dueOn: string | null;
  catchUpUntil: string | null;
  amountCents: number;
  coverageCents: number;
  covered: boolean;
  highlight: WalletHighlight;
  earlier: boolean;
};

/**
 * One row per position; the open positions before a custom „Von“ come first as a
 * single row. A position is covered while its coverage is not negative.
 */
export const upcomingRows = (t: TFunction, upcoming: WalletUpcomingPayments): UpcomingRow[] => {
  const { earlier } = upcoming;

  const positions = upcoming.results.map<UpcomingRow>((position) => ({
    key: position.key,
    label: positionLabel(t, position),
    persons: position.recipient_count,
    dueOn: position.due_date,
    catchUpUntil: position.catch_up_until,
    amountCents: position.amount_cents,
    coverageCents: position.coverage_cents,
    covered: position.is_covered,
    highlight: position.highlight,
    earlier: false
  }));

  if (!earlier) return positions;

  return [
    {
      key: 'earlier',
      label: t('pxtraCard:upcomingEarlier', { month: monthYearName(upcoming.from) }),
      persons: earlier.recipients_total,
      dueOn: null,
      catchUpUntil: null,
      amountCents: earlier.amount_cents,
      coverageCents: earlier.coverage_cents,
      covered: earlier.coverage_cents >= 0,
      highlight: earlier.highlight,
      earlier: true
    },
    ...positions
  ];
};

export const coverageTotal = (rows: UpcomingRow[]) =>
  rows.reduce((sum, row) => sum + row.coverageCents, 0);
